import {
  AloftArea,
  AloftPrice,
  LocationType,
  ManualAdjustmentKey,
  ORDER_FEATURES,
  isDefined,
  orUndefined,
  schAlertType,
  schAloftArea,
  schAloftPrice,
  schLocationType,
  schPairedCostRouteItem,
} from '@aloft/toolkit-shared';

import { PropertyGridCellKey } from '../../../../components/PropertyGrid/types.ts';
import { CompAdjustments } from '../../../../redux/features/selectors/CompAdjustments.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import {
  isBathAdjustmentMaterial,
  isConditionAdjustmentMaterial,
  isGarageCarportAdjustmentMaterial,
  isGlaAdjustmentMaterial,
  isPresentOrNotAdjustmentMaterial,
  isQualityAdjustmentMaterial,
  isSiteSizeAdjustmentMaterial,
} from '../utils/compAdjustmentMateriality.ts';
import { getComparableDisplayName, getScopedAlertType } from '../utils/compTypeScoping.ts';

type SubjectData = SubjectPropertyDataState['subjectData']['editableData'];
type CompData = CompPropertyDataState['compData']['editableData'];

type FeatureCheck = {
  label: string;
  cellKey: ManualAdjustmentKey;
  getSubjectValue: (subject: SubjectData) => unknown;
  getCompValue: (comp: CompData) => unknown;
  getAdjustment: (compAdjustments: CompAdjustments) => AloftPrice | undefined;
  isMaterialDifference: (params: {
    subjectValue: unknown;
    compValue: unknown;
    locationType?: LocationType;
  }) => boolean;
};

const TARGET_CELL_TOOLTIP = 'Adjustment is missing';

/**
 * Materiality fields
 *
 * Always-present adjustment grid rows.
 * Fires when a material difference exists between comp and subject but no adjustment is recorded.
 */
const glaFeatureCheck: FeatureCheck = {
  label: 'GLA',
  cellKey: 'gla',
  getSubjectValue: (subject) => orUndefined(schAloftArea.parse)(subject.improvements?.gla),
  getCompValue: (comp) => orUndefined(schAloftArea.parse)(comp.improvements?.gla),
  getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.mainDwelling?.gla?.adjustment),
  isMaterialDifference: ({ subjectValue, compValue }) =>
    isGlaAdjustmentMaterial(subjectValue as AloftArea, compValue as AloftArea),
};

const siteSizeFeatureCheck: FeatureCheck = {
  label: 'Site Size',
  cellKey: 'site',
  getSubjectValue: (subject) => orUndefined(schAloftArea.parse)(subject.site?.area),
  getCompValue: (comp) => orUndefined(schAloftArea.parse)(comp.site?.area),
  getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.site?.size?.adjustment),
  isMaterialDifference: ({ subjectValue, compValue, locationType }) =>
    isSiteSizeAdjustmentMaterial(subjectValue as AloftArea, compValue as AloftArea, locationType),
};

const conditionFeatureCheck: FeatureCheck = {
  label: 'Condition',
  cellKey: 'condition',
  getSubjectValue: (subject) => {
    const v = subject.improvements?.uadCondition;
    return isDefined(v) ? v : undefined;
  },
  getCompValue: (comp) => {
    const v = comp.improvements?.uadCondition;
    return isDefined(v) ? v : undefined;
  },
  getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.condition?.adjustment),
  isMaterialDifference: ({ subjectValue, compValue }) =>
    isConditionAdjustmentMaterial(subjectValue as number, compValue as number),
};

const qualityFeatureCheck: FeatureCheck = {
  label: 'Quality',
  cellKey: 'quality',
  getSubjectValue: (subject) => {
    const v = subject.improvements?.uadQuality;
    return isDefined(v) ? v : undefined;
  },
  getCompValue: (comp) => {
    const v = comp.improvements?.uadQuality;
    return isDefined(v) ? v : undefined;
  },
  getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.quality?.adjustment),
  isMaterialDifference: ({ subjectValue, compValue }) =>
    isQualityAdjustmentMaterial(subjectValue as number, compValue as number),
};

const bathFeatureCheck: FeatureCheck = {
  label: 'Baths',
  cellKey: 'bathAboveGrade',
  getSubjectValue: (subject) => subject.improvements,
  getCompValue: (comp) => comp.improvements,
  getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.bathAboveGrade?.adjustment),
  isMaterialDifference: ({ subjectValue, compValue }) => {
    const s = subjectValue as CompData['improvements'];
    const c = compValue as CompData['improvements'];
    return isBathAdjustmentMaterial(s?.fullBaths, s?.halfBaths, c?.fullBaths, c?.halfBaths);
  },
};

const garageCarportFeatureCheck: FeatureCheck = {
  label: 'Garage/Carport',
  cellKey: 'garageCarport',
  getSubjectValue: (subject) => subject.improvements,
  getCompValue: (comp) => comp.improvements,
  getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.garageCarport?.adjustment),
  isMaterialDifference: ({ subjectValue, compValue }) => {
    const s = subjectValue as CompData['improvements'];
    const c = compValue as CompData['improvements'];
    return isGarageCarportAdjustmentMaterial({
      subjectHasFeature:
        ORDER_FEATURES.garage.hasFeature({ garage: s?.garage } as never) ||
        ORDER_FEATURES.carport.hasFeature({ carport: s?.carport } as never) ||
        ORDER_FEATURES.detachedGarage.hasFeature({ detachedGarage: s?.detachedGarage } as never),
      compHasFeature:
        ORDER_FEATURES.garage.hasFeature({ garage: c?.garage } as never) ||
        ORDER_FEATURES.carport.hasFeature({ carport: c?.carport } as never) ||
        ORDER_FEATURES.detachedGarage.hasFeature({ detachedGarage: c?.detachedGarage } as never),
      subjectSpaces: s?.garage?.spaces ?? s?.carport?.spaces,
      compSpaces: c?.garage?.spaces ?? c?.carport?.spaces,
      subjectParkingType: s?.garage ? 'garage' : 'carport',
      compParkingType: c?.garage ? 'garage' : 'carport',
    });
  },
};

const MATERIALITY_CHECKS: FeatureCheck[] = [
  glaFeatureCheck,
  siteSizeFeatureCheck,
  conditionFeatureCheck,
  qualityFeatureCheck,
  bathFeatureCheck,
  garageCarportFeatureCheck,
];

/**
 * Binary presence / absence fields
 *
 * Fires when a feature is present on one property but absent on the other and no adjustment is recorded.
 */
type PresenceCheck = {
  label: string;
  cellKey: ManualAdjustmentKey;
  subjectHasFeature: (subject: SubjectData) => boolean;
  compHasFeature: (comp: CompData) => boolean;
  getAdjustment: (compAdjustments: CompAdjustments) => AloftPrice | undefined;
};

const PRESENCE_CHECKS: PresenceCheck[] = [
  {
    label: 'Pool/Spa',
    cellKey: 'poolSpa',
    subjectHasFeature: (s) =>
      ORDER_FEATURES.pool.hasFeature(s.improvements as never) ||
      ORDER_FEATURES.spa.hasFeature(s.improvements as never),
    compHasFeature: (c) =>
      ORDER_FEATURES.pool.hasFeature(c.improvements as never) ||
      ORDER_FEATURES.spa.hasFeature(c.improvements as never),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.pool?.adjustment),
  },
  {
    label: 'Porch/Patio/Deck',
    cellKey: 'porchPatioDeck',
    subjectHasFeature: (s) =>
      ORDER_FEATURES.porch.hasFeature(s.improvements as never) ||
      ORDER_FEATURES.patio.hasFeature(s.improvements as never) ||
      ORDER_FEATURES.deck.hasFeature(s.improvements as never),
    compHasFeature: (c) =>
      ORDER_FEATURES.porch.hasFeature(c.improvements as never) ||
      ORDER_FEATURES.patio.hasFeature(c.improvements as never) ||
      ORDER_FEATURES.deck.hasFeature(c.improvements as never),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.porchPatioDeck?.adjustment),
  },
  {
    label: 'Attic',
    cellKey: 'attic',
    subjectHasFeature: (s) => ORDER_FEATURES.attic.hasFeature(s.improvements as never),
    compHasFeature: (c) => ORDER_FEATURES.attic.hasFeature(c.improvements as never),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.attic?.adjustment),
  },
  {
    label: 'Fireplace',
    cellKey: 'fireplace',
    subjectHasFeature: (s) => ORDER_FEATURES.fireplace.hasFeature(s.improvements as never),
    compHasFeature: (c) => ORDER_FEATURES.fireplace.hasFeature(c.improvements as never),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.fireplace?.adjustment),
  },
  {
    label: 'Barn',
    cellKey: 'barn',
    subjectHasFeature: (s) => ORDER_FEATURES.barn.hasFeature(s.improvements as never),
    compHasFeature: (c) => ORDER_FEATURES.barn.hasFeature(c.improvements as never),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.barn?.adjustment),
  },
  {
    label: 'ADU',
    cellKey: 'adu',
    subjectHasFeature: (s) => ORDER_FEATURES.adu.hasFeature(s.improvements as never),
    compHasFeature: (c) => ORDER_FEATURES.adu.hasFeature(c.improvements as never),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.adu?.adjustment),
  },
  {
    label: 'Shed',
    cellKey: 'shed',
    subjectHasFeature: (s) => ORDER_FEATURES.shed.hasFeature(s.improvements as never),
    compHasFeature: (c) => ORDER_FEATURES.shed.hasFeature(c.improvements as never),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.shed?.adjustment),
  },
  {
    label: 'Workshop',
    cellKey: 'workshop',
    subjectHasFeature: (s) => ORDER_FEATURES.workshop.hasFeature(s.improvements as never),
    compHasFeature: (c) => ORDER_FEATURES.workshop.hasFeature(c.improvements as never),
    getAdjustment: (ca) => orUndefined(schAloftPrice.parse)(ca.workshop?.adjustment),
  },
];

export const missingCompAdjustment: AlertDefinition = ({
  compAdjustments,
  compSequence,
  compType,
  order,
  propertyDataEntities,
}) => {
  const subjectData = (propertyDataEntities.subject as SubjectPropertyDataState | undefined)
    ?.subjectData.editableData;

  if (!subjectData) return undefined;

  const locationType = orUndefined(schLocationType.parse)(
    subjectData.propertyIdentification?.locationType,
  );

  const fixRoute =
    schPairedCostRouteItem.enum[order.isInspectifyOrder ? 'adjustments-grid' : 'adjustments'];
  const triggers = getAlertTriggers({ fixRoute, order, includeUpdate: true });

  type Hit =
    | { kind: 'materiality'; id: string; check: FeatureCheck }
    | { kind: 'presence'; id: string; check: PresenceCheck; subjectHas: boolean; compHas: boolean };

  const hit = compSequence
    .flatMap((id): Hit[] => {
      const compData = (propertyDataEntities[id] as CompPropertyDataState | undefined)?.compData
        .editableData;
      const compAdjustmentData = compAdjustments[id];
      if (!compData || !compAdjustmentData) return [];

      // Materiality gap
      const materialityHit = MATERIALITY_CHECKS.find((check) => {
        const subjectValue = check.getSubjectValue(subjectData);
        const compValue = check.getCompValue(compData);
        const adjustment = check.getAdjustment(compAdjustmentData);

        if (!isDefined(subjectValue) || !isDefined(compValue)) return false;
        if (!check.isMaterialDifference({ subjectValue, compValue, locationType })) return false;

        return !isDefined(adjustment) || adjustment.equals(AloftPrice.zero);
      });

      if (materialityHit) {
        return [{ kind: 'materiality', id, check: materialityHit }];
      }

      // Presence / absence mismatch
      const presenceHit = PRESENCE_CHECKS.find((check) => {
        const subjectHas = check.subjectHasFeature(subjectData);
        const compHas = check.compHasFeature(compData);
        if (!isPresentOrNotAdjustmentMaterial(subjectHas, compHas)) return false;

        const adjustment = check.getAdjustment(compAdjustmentData);
        return !isDefined(adjustment) || adjustment.equals(AloftPrice.zero);
      });

      if (!presenceHit) return [];

      return [
        {
          kind: 'presence',
          id,
          check: presenceHit,
          subjectHas: presenceHit.subjectHasFeature(subjectData),
          compHas: presenceHit.compHasFeature(compData),
        },
      ];
    })
    .at(0);

  if (!hit) return undefined;

  const { id } = hit;
  const propertyName = getComparableDisplayName(id, compSequence);
  const { label, cellKey } = hit.check;
  const targetCellKey = `${id}-${cellKey}` as PropertyGridCellKey;

  if (hit.kind === 'materiality') {
    return {
      type: getScopedAlertType(schAlertType.enum.missingCompAdjustment, compType),
      status: 'active',
      severity: 'error',
      orderType: ['saas', 'appraisify'],
      message: `${propertyName} has a material ${label} difference from the Subject but no adjustment is shown.`,
      propertyID: id,
      ...(compType ? { compType } : {}),
      fixingInfo: {
        tips: `Since the Comp has a material ${label} difference from the Subject, an adjustment is expected. Alternatively, explain why the market does not react to this difference.`,
        pairedCostRouteItem: fixRoute,
        fixingMethod: ['comment', 'action'],
        commentAlwaysRequired: false,
        targetCellKey,
        targetCellTooltip: TARGET_CELL_TOOLTIP,
      },
      triggers,
    };
  }

  const { subjectHas, compHas } = hit;
  const subjectVerb = subjectHas ? 'has' : 'does not have';
  const compVerb = compHas ? 'has' : 'does not have';
  const adjDirection = compHas ? 'negative' : 'positive';

  return {
    type: getScopedAlertType(schAlertType.enum.missingCompAdjustment, compType),
    status: 'active',
    severity: 'error',
    orderType: ['saas', 'appraisify'],
    message: `${propertyName} ${compVerb} a ${label} while the Subject ${subjectVerb}, but no adjustment is shown.`,
    propertyID: id,
    ...(compType ? { compType } : {}),
    fixingInfo: {
      tips: `Since the Subject ${subjectVerb} a ${label}, a ${adjDirection} adjustment is expected for ${propertyName}. Alternatively, explain why the market does not react to this difference.`,
      pairedCostRouteItem: fixRoute,
      fixingMethod: ['comment', 'action'],
      commentAlwaysRequired: false,
      targetCellKey,
      targetCellTooltip: TARGET_CELL_TOOLTIP,
    },
    triggers,
  };
};
